import { Linking, View } from 'react-native';
import { setStringAsync } from 'expo-clipboard';
import { Divider } from 'react-native-elements';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { Icon } from '@/components/Icon';
import { Text } from '@/components/Text';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { SubMenuItem as SubMenuItemType } from '@/components/ui/therapist/CollapsibleSidebar';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { useOnHover } from '@/hooks/useOnHover';

import { styles } from './styles';
import theme from '@/config/theme';
import { logError } from '@/utils/logHandling';
import { useDialog } from '@/hooks/useDialog';

export const SubMenuItem = ({
  setExpandedSubMenu,
  subItem,
}: {
  setExpandedSubMenu: (submenu: SubMenuItemType[] | null) => void;
  subItem: SubMenuItemType;
}) => {
  const { icon, label, screen, url } = subItem;
  const { isHovered, handleHoverIn, handleHoverOut } = useOnHover();
  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const { showDialog, removeDialog } = useDialog();

  const showUrlFallbackDialog = () => {
    showDialog({
      title: 'Fehler beim Öffnen der Links',
      showCancelButton: true,
      confirmAction: {
        label: 'In Zwischenablage kopieren',
        onPress: async () => {
          await setStringAsync(url ?? '');
          removeDialog();
        },
      },
      cancelAction: {
        label: 'Abbrechen',
        onPress: () => removeDialog(),
      },
      contentComponent: () => (
        <>
          <Text>
            Sie können die Adresse in die Zwischenablage kopieren und manuell in
            Ihrem Browser öffnen.
          </Text>
          <PressableNeolexon
            onPress={async () => await setStringAsync(url ?? '')}
          >
            <Text style={styles.urlText}>{url}</Text>
          </PressableNeolexon>
        </>
      ),
    });
  };

  const onMenuItemPress = async () => {
    if (!url) {
      navigate(screen as any);
      setExpandedSubMenu(null);
      return;
    }
    try {
      if (!(await Linking.canOpenURL(url))) {
        showUrlFallbackDialog();
        logError(`Linking was unable to open URL`, null, {
          url,
        });
        return;
      }
      await Linking.openURL(url);
      setExpandedSubMenu(null);
    } catch (error) {
      showUrlFallbackDialog();
      logError(`Encountered an error opening Linking URL`, error, {
        url,
      });
    }
  };

  return (
    <>
      <PressableNeolexon
        onPress={onMenuItemPress}
        onHoverIn={() => handleHoverIn()}
        onHoverOut={() => handleHoverOut()}
        testID={label.replace(' ', '-').toLowerCase()}
      >
        <View
          style={[
            styles.menuItemContainer,
            isHovered && styles.isSelected,
            { display: 'flex', justifyContent: 'space-between' },
          ]}
        >
          <Text style={styles.subMenuItem}>{label}</Text>
          {icon ? <Icon name={icon} size={15} /> : null}
        </View>
      </PressableNeolexon>
      <Divider color={theme.colors.lightGrey} width={2} />
    </>
  );
};
